import CreditsDetail from '@affine/core/modules/app-sidebar/views/credits-detail';
import * as styles from './index.css';
import { useI18n } from '@affine/i18n';
import { useEffect, useState } from 'react';
import { useLiveData, useService } from '@toeverything/infra';
import { UserCreditsDetailsService } from '@affine/core/modules/cloud/services/user-credits-detail';
import dayjs from 'dayjs';
import { Pagination, ConfigProvider } from 'antd';
import IconFont from '@ys/repo-ui/base/IconFont';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { useParams } from 'react-router-dom';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import blackLoadingImage from '../../../../../../public/imgs/black_loading.webp';
import { WorkbenchService } from '@affine/core/modules/workbench';

interface Transaction {
  transactionId: number;
  walletId: number;
  userId: number;
  transactionType: string | null;
  amount: string;
  balanceBefore: string;
  balanceAfter: string;
  businessOrderId: string;
  remark: string;
  createTime: number;
  extra: {
    type: string;
    cid: string;
  } | null;
}
const Credits = () => {
  const t = useI18n();
  const params = useParams();
  const { jumpToPage } = useNavigateHelper();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const creditsDetail = useService(UserCreditsDetailsService).creditsDetail;
  const currentWorkspace = useService(WorkspaceService).workspace;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const workbench = useService(WorkbenchService).workbench;
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);

  const [total, setTotal] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const fetchTransactions = async (isInit?: boolean) => {
    try {
      setIsError(false);

      const res = await creditsDetail.fetchTransactionsList({ page, pageSize });
      if (res?.data?.transactions) {
        setTransactions(res.data?.transactions);
        setTotal(res.data?.total);
      }

      if (isInit) {
        setIsLoading(false);
      }
    } catch (e) {
      setIsError(true);
    }
  };
  useEffect(() => {
    fetchTransactions(true);
  }, [page, pageSize]);

  return (
    <div className={`${styles.creditsContainer} `}>
      <div className={styles.creditsTitle}>
        {t['com.muset.settings.credits.title']()}
      </div>
      <div className={styles.creditsDetailWrapper}>
        <CreditsDetail position="settingDialog" />
      </div>

      <div className={styles.transactionsTable}>
        <div className={styles.tableHeader}>
          <div className={styles.detailsCol}>
            {t['com.muset.settings.credits.table.header.details']()}
          </div>
          <div className={styles.dateCol}>
            {t['com.muset.settings.credits.table.header.date']()}
          </div>
          <div className={styles.creditsCol}>
            {t['com.muset.settings.credits.table.header.credits']()}
          </div>
        </div>

        <div className={styles.tableBody}>
          {isLoading ? (
            <div className={styles.loadingImageWrapper}>
              <img
                src={blackLoadingImage}
                alt="Loading"
                className={styles.loadingImage}
              />
            </div>
          ) : isError ? (
            <div className={styles.loadingImageWrapper}>
              <div
                className={styles.errorRetry}
                onClick={() => fetchTransactions()}
              >
                {t['com.muset.settings.credits.table.error']()}
              </div>
            </div>
          ) : (
            transactions?.map(transaction => (
              <div key={transaction.transactionId} className={styles.tableRow}>
                <div
                  className={`${styles.detailsCol} ${transaction.extra?.type === 'CHAT' && transaction.extra?.cid ? styles.detailsColWithIcon : ''}`}
                  onClick={() => {
                    if (
                      transaction.extra?.type === 'CHAT' &&
                      transaction.extra?.cid
                    ) {
                      workspaceDialogService.closeAll();
                      const pathList = window.location.pathname.split('/');
                      if (!sidebarOpen) {
                        workbench.toggleSidebar();
                      }
                      if (
                        pathList[pathList.length - 1] !== 'home' &&
                        !params.pageId
                      ) {
                        jumpToPage(
                          currentWorkspace.id,
                          'home',
                          undefined,
                          transaction.extra.cid
                        );
                        return;
                      }
                      window.dispatchEvent(
                        new CustomEvent('changeChatUrl', {
                          detail: { cid: transaction.extra.cid },
                        })
                      );
                    }
                  }}
                >
                  <div className={styles.detailsColText}>
                    {transaction.remark}
                  </div>

                  {transaction.extra?.type === 'CHAT' &&
                    transaction.extra?.cid && (
                      <IconFont
                        className={styles.detailsColIcon}
                        name="leftArrow"
                        size={16}
                      />
                    )}
                </div>
                <div className={styles.dateCol}>
                  {dayjs(transaction.createTime).format('YYYY-MM-DD HH:mm')}
                </div>
                <div className={styles.creditsCol}>
                  {transaction.transactionType === 'DEBIT' ? '-' : ''}
                  {transaction.amount.split('.')[0]}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {!isLoading && (
        <div className={styles.paginationWrapper}>
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  itemBg: 'transparent',
                  itemActiveBg: 'transparent',
                  itemActiveColor: '#000',
                  borderRadius: 0,
                  colorBorder: 'transparent',
                },
              },
            }}
          >
            <Pagination
              current={page}
              defaultPageSize={5}
              pageSize={5}
              showSizeChanger={false}
              showTitle={false}
              total={total}
              onChange={(page, pageSize) => {
                setPage(page);
              }}
            />
          </ConfigProvider>
        </div>
      )}
    </div>
  );
};
export default Credits;
